import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './PositionProgressModal.css';

// График изменения места участника в личном зачете по этапам.
// points: [{ stage, routeName, position, finished }]
const PositionProgressModal = ({ isOpen, onClose, riderName, points }) => {
  const { t } = useLanguage();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isOpen) return null;

  const data = (points || []).filter(p => p.position !== null && p.position !== undefined);

  // Геометрия графика. На мобильных система координат близка к ширине экрана,
  // поэтому подписи осей не уменьшаются при масштабировании SVG
  const width = isMobile ? 380 : 640;
  const height = isMobile ? 300 : 320;
  const margin = isMobile
    ? { top: 20, right: 16, bottom: 42, left: 38 }
    : { top: 24, right: 28, bottom: 44, left: 52 };
  const pointRadius = isMobile ? 7 : 8;
  const hitRadius = isMobile ? 16 : 18;
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;

  const positions = data.map(p => p.position);
  const bestPosition = positions.length ? Math.min(...positions) : 1;
  const worstPosition = positions.length ? Math.max(...positions) : 1;

  // Ось Y перевернута: первое место сверху. Домен слегка расширен, чтобы точки не липли к краям
  const yMin = Math.max(1, bestPosition - 1);
  const yMax = worstPosition + 1;

  const xFor = (index) => {
    if (data.length === 1) return margin.left + plotWidth / 2;
    return margin.left + (plotWidth * index) / (data.length - 1);
  };

  const yFor = (position) => {
    if (yMax === yMin) return margin.top + plotHeight / 2;
    return margin.top + (plotHeight * (position - yMin)) / (yMax - yMin);
  };

  // Целые деления оси Y (не больше шести)
  const buildYTicks = () => {
    const span = yMax - yMin;
    const step = Math.max(1, Math.ceil(span / 5));
    const ticks = [];
    for (let value = yMin; value <= yMax; value += step) {
      ticks.push(value);
    }
    if (ticks[ticks.length - 1] !== yMax) ticks.push(yMax);
    return ticks;
  };

  const yTicks = buildYTicks();

  // Линия строится ломаной: соседние этапы соединяются прямыми отрезками,
  // без сглаживания — промежуточных значений между этапами не существует
  const buildLinePath = () => {
    return data
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(p.position)}`)
      .join(' ');
  };

  const linePath = buildLinePath();
  const hovered = hoveredIndex !== null ? data[hoveredIndex] : null;

  // Разбиваем длинное название этапа на строки по ширине подсказки
  const wrapText = (text, maxChars, maxLines) => {
    const words = String(text).split(/\s+/).filter(Boolean);
    const lines = [];
    let current = '';

    words.forEach(word => {
      const candidate = current ? `${current} ${word}` : word;

      if (candidate.length <= maxChars) {
        current = candidate;
        return;
      }

      if (current) lines.push(current);
      // Слово длиннее строки режем по символам
      current = word.length > maxChars ? `${word.slice(0, maxChars - 1)}…` : word;
    });

    if (current) lines.push(current);

    if (lines.length > maxLines) {
      const kept = lines.slice(0, maxLines);
      kept[maxLines - 1] = `${kept[maxLines - 1].slice(0, Math.max(1, maxChars - 1))}…`;
      return kept;
    }

    return lines;
  };

  const renderTooltip = () => {
    if (!hovered) return null;

    const fontSize = isMobile ? 13 : 12;
    // Оценка ширины символа с запасом - кириллица и жирный заголовок шире латиницы
    const charWidth = fontSize * 0.62;
    const paddingX = 10;
    const lineHeight = fontSize + 4;

    // Подсказка не шире области графика и не растягивается на всю ширину
    const maxBoxWidth = Math.min(isMobile ? 300 : 320, width - 24);
    const maxChars = Math.max(10, Math.floor((maxBoxWidth - paddingX * 2) / charWidth));

    const titleLine = `${t('results.progress_stage')} ${hovered.stage}: ${t('results.position')} ${hovered.position}`;
    const subText = hovered.finished
      ? (hovered.routeName || '')
      : t('results.progress_not_finished');
    const subLines = subText ? wrapText(subText, maxChars, 2) : [];
    const allLines = [titleLine, ...subLines];

    const longest = allLines.reduce((max, line) => Math.max(max, line.length), 0);
    const boxWidth = Math.min(maxBoxWidth, Math.max(110, longest * charWidth + paddingX * 2));
    const boxHeight = 12 + allLines.length * lineHeight;

    const x = xFor(hoveredIndex);
    const y = yFor(hovered.position);

    // Прижимаем подсказку к области графика, чтобы она не выходила за край
    const boxX = Math.min(
      Math.max(x - boxWidth / 2, Math.min(margin.left, 12)),
      width - Math.min(margin.right, 12) - boxWidth
    );
    const boxY = y - boxHeight - 14 < margin.top ? y + 14 : y - boxHeight - 14;

    return (
      <g className="position-progress-tooltip" pointerEvents="none">
        <rect x={boxX} y={boxY} width={boxWidth} height={boxHeight} rx="6" />
        {allLines.map((line, index) => (
          <text
            key={`tooltip-line-${index}`}
            x={boxX + paddingX}
            y={boxY + 6 + lineHeight * index + fontSize}
            className={index === 0 ? '' : 'position-progress-tooltip-sub'}
          >
            {line}
          </text>
        ))}
      </g>
    );
  };

  return (
    <div className="position-progress-overlay" onClick={onClose}>
      <div className="position-progress-modal" onClick={(e) => e.stopPropagation()}>
        <div className="position-progress-header">
          <div>
            <h3 className="position-progress-title">{t('results.progress_chart_title')}</h3>
            <div className="position-progress-subtitle">{riderName}</div>
          </div>
          <button className="position-progress-close" onClick={onClose} aria-label={t('common.close')}>
            &times;
          </button>
        </div>

        <div className="position-progress-body">
          {data.length === 0 ? (
            <div className="position-progress-empty">{t('results.progress_no_data')}</div>
          ) : (
            <>
              <svg
                className="position-progress-chart"
                viewBox={`0 0 ${width} ${height}`}
                role="img"
                aria-label={`${t('results.progress_chart_title')}: ${riderName}`}
              >
                {/* Сетка и подписи оси Y (места) */}
                {yTicks.map(tick => (
                  <g key={`y-${tick}`}>
                    <line
                      className="position-progress-grid"
                      x1={margin.left}
                      x2={width - margin.right}
                      y1={yFor(tick)}
                      y2={yFor(tick)}
                    />
                    <text
                      className="position-progress-axis-label"
                      x={margin.left - 10}
                      y={yFor(tick) + 4}
                      textAnchor="end"
                    >
                      {tick}
                    </text>
                  </g>
                ))}

                {/* Подписи оси X (этапы) */}
                {data.map((p, i) => (
                  <text
                    key={`x-${p.stage}`}
                    className="position-progress-axis-label"
                    x={xFor(i)}
                    y={height - margin.bottom + 20}
                    textAnchor="middle"
                  >
                    {p.stage}
                  </text>
                ))}

                <text
                  className="position-progress-axis-title"
                  x={margin.left + plotWidth / 2}
                  y={height - 8}
                  textAnchor="middle"
                >
                  {t('results.progress_stage')}
                </text>
                <text
                  className="position-progress-axis-title"
                  x={-(margin.top + plotHeight / 2)}
                  y={14}
                  transform="rotate(-90)"
                  textAnchor="middle"
                >
                  {t('results.position')}
                </text>

                {/* Линия прогресса */}
                <path className="position-progress-line" d={linePath} />

                {data.map((p, i) => (
                  <g key={`point-${p.stage}`}>
                    <circle
                      className={`position-progress-point ${p.finished ? '' : 'position-progress-point-empty'}`}
                      cx={xFor(i)}
                      cy={yFor(p.position)}
                      r={pointRadius}
                    />
                    {/* Увеличенная область наведения */}
                    <circle
                      className="position-progress-hit"
                      cx={xFor(i)}
                      cy={yFor(p.position)}
                      r={hitRadius}
                      onMouseEnter={() => setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      onClick={() => setHoveredIndex(i)}
                    >
                      <title>
                        {`${t('results.progress_stage')} ${p.stage}: ${t('results.position')} ${p.position}`}
                      </title>
                    </circle>
                  </g>
                ))}

                {/* Подписываем только первую и последнюю точки */}
                {[0, data.length - 1].filter((v, i, arr) => arr.indexOf(v) === i).map(index => (
                  <text
                    key={`label-${index}`}
                    className="position-progress-point-label"
                    x={xFor(index)}
                    y={yFor(data[index].position) - pointRadius - 7}
                    textAnchor="middle"
                  >
                    {data[index].position}
                  </text>
                ))}

                {renderTooltip()}
              </svg>

              <div className="position-progress-caption">{t('results.progress_caption')}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PositionProgressModal;
